import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Tab="TASKS"|"ALERTS"|"DASHBOARD"|"SCHOOLWIDE"|"EXPORT"|"YEAR_END"|"HANDOVER";
type Task={
  taskType:string;priority:string;refId:string;learnerId?:string|null;displayName?:string|null;
  title:string;subtitle?:string|null;dueAt?:string|null;actionPath:string;
};
type TaskCenter={
  counts:{total:number;high:number;overdueCounseling:number;followUp:number;transfer:number;bulkFailure:number;badge:number;evidence:number;handover:number;careerTask?:number;schoolCheck?:number};
  items:Task[];
};
type AlertItem={
  alertKey:string;severity:string;category:string;title:string;message:string;
  dueAt?:string|null;actionPath:string;state:"OPEN"|"SNOOZED"|"ACKNOWLEDGED";snoozedUntil?:string|null;
};
type AlertCenter={counts:{open:number;high:number;snoozed:number;acknowledged:number};items:AlertItem[]};
type ClassMetric={
  classGroupId:string;name:string;gradeLevel:string;classLabel:string;studentCount:number;
  assessmentAssigned:number;assessmentCompleted:number;participationCount:number;participationCompleted:number;
  verifiedEvidence:number;openCounseling:number;scheduledCounseling:number;overdueCounseling:number;
  activeBadges:number;badgeDeliveryWaiting:number;
};
type ClassDashboard={academicYearId?:string|null;classes:ClassMetric[]};
type AcademicYear={academicYearId:string;yearCode:string;startsOn:string;endsOn:string;status:string};
type Lifecycle={canManage:boolean;academicYears:AcademicYear[]};
type Preflight={
  sourceAcademicYearId:string;sourceYearCode:string;targetAcademicYearId:string;targetYearCode:string;targetStartsOn:string;canClose:boolean;
  blockers:{openTransfers:number;unpreparedStudents:number;preparedTransitionBatches:number;targetNotStarted:boolean};
  carryover:{openCounselingCases:number;scheduledCounseling:number;failedBulkJobs:number;pendingBadgeManagement:number};
};
type HandoverItem={itemId:string;itemType:string;learnerId?:string|null;sourceRefId:string;title:string;snapshot:Record<string,unknown>};
type HandoverPackage={
  packageId:string;sourcePersonId:string;sourceName:string;targetPersonId:string;targetName:string;
  status:string;note?:string|null;createdAt:string;acknowledgedAt?:string|null;incoming:boolean;itemCount:number;items:HandoverItem[];
};
type HandoverWorkspace={staff:Array<{personId:string;displayName:string}>;packages:HandoverPackage[]};
type SchoolClass={classGroupId:string;name:string;gradeLevel:string;classLabel:string;studentCount:number};
type SchoolAssessment={versionId:string;instrumentName:string;title:string;gradeMin?:number|null;gradeMax?:number|null;estimatedMinutes?:number|null};
type SchoolJob={jobId:string;status:string;requestedCount:number;succeededCount:number;skippedCount:number;failedCount:number;retryNumber:number;createdAt:string;versionId?:string|null};
type Schoolwide={academicYearId?:string|null;classes:SchoolClass[];assessments:SchoolAssessment[];recentJobs:SchoolJob[]};

function navigate(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
function fmt(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
}
function priorityLabel(value:string){return value==="HIGH"?"우선":"일반";}
function taskTypeLabel(value:string){
  const map:Record<string,string>={
    COUNSELING_OVERDUE:"상담",FOLLOW_UP:"후속상담",TRANSFER:"학적",BULK_JOB:"일괄작업",
    IMPORT_RUN:"명단",BADGE_MANAGEMENT:"디지털배지",BADGE_DELIVERY:"배지수령",
    EVIDENCE_REVIEW:"결과물",HANDOVER:"인수인계",CAREER_TASK:"진로업무",ASSURANCE_CONFIRMATION:"학교확인"
  };
  return map[value] || value;
}
function alertCategoryLabel(value:string){
  const map:Record<string,string>={
    CLASS:"학급",ROSTER:"명단",YEAR_END:"학년도",COUNSELING_OVERDUE:"상담",FOLLOW_UP:"상담",
    TRANSFER:"학적",BULK_JOB:"일괄작업",IMPORT_RUN:"명단",BADGE_MANAGEMENT:"배지",BADGE_DELIVERY:"배지",EVIDENCE_REVIEW:"검토",HANDOVER:"인수인계"
  };
  return map[value] || value;
}
function csvCell(value:unknown){
  const text=value==null?"":String(value);
  return '"'+text.replace(/"/g,'""')+'"';
}
function downloadCsv(rows:Array<Record<string,unknown>>,name:string){
  if(!rows.length)return;
  const headers=Array.from(new Set(rows.flatMap((row)=>Object.keys(row))));
  const content=[
    headers.map(csvCell).join(","),
    ...rows.map((row)=>headers.map((key)=>csvCell(row[key])).join(","))
  ].join("\r\n");
  const blob=new Blob(["\uFEFF",content],{type:"text/csv;charset=utf-8"});
  const url=URL.createObjectURL(blob);
  const anchor=document.createElement("a");
  anchor.href=url;anchor.download=name;anchor.click();
  URL.revokeObjectURL(url);
}

export function CenterOperationsPage({client,schoolId}:{client:PlatformClient;schoolId:string}){
  const initialParams=new URLSearchParams(window.location.search);
  const initialTab=initialParams.get("tab");
  const [tab,setTab]=useState<Tab>(
    initialTab==="alerts" ? "ALERTS" :
    initialTab==="dashboard" ? "DASHBOARD" :
    initialTab==="schoolwide" ? "SCHOOLWIDE" :
    initialTab==="export" ? "EXPORT" :
    initialTab==="year-end" ? "YEAR_END" :
    initialTab==="handover" ? "HANDOVER" :
    "TASKS"
  );
  const [tasks,setTasks]=useState<TaskCenter|null>(null);
  const [alerts,setAlerts]=useState<AlertCenter|null>(null);
  const [dashboard,setDashboard]=useState<ClassDashboard|null>(null);
  const [lifecycle,setLifecycle]=useState<Lifecycle|null>(null);
  const [handover,setHandover]=useState<HandoverWorkspace|null>(null);
  const [schoolwide,setSchoolwide]=useState<Schoolwide|null>(null);
  const [refresh,setRefresh]=useState(0);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");

  const [targetYearId,setTargetYearId]=useState("");
  const [preflight,setPreflight]=useState<Preflight|null>(null);
  const [closeConfirm,setCloseConfirm]=useState("");
  const [closeNote,setCloseNote]=useState("");
  const [handoverTarget,setHandoverTarget]=useState("");
  const [handoverNote,setHandoverNote]=useState("");

  const [alertFilter,setAlertFilter]=useState<"OPEN"|"ALL">("OPEN");
  const [schoolAssessment,setSchoolAssessment]=useState("");
  const [selectedClasses,setSelectedClasses]=useState<string[]>([]);

  useEffect(()=>{
    let active=true;
    setError("");
    Promise.all([
      client.rpc("center_task_center_v2",{p_school_id:schoolId}),
      client.rpc("center_alert_center",{p_school_id:schoolId}),
      client.rpc("center_class_operations_dashboard",{p_school_id:schoolId,p_academic_year_id:null}),
      client.rpc("center_student_lifecycle_workspace",{p_school_id:schoolId}),
      client.rpc("center_handover_workspace",{p_school_id:schoolId}),
      client.rpc("center_schoolwide_operations_workspace",{p_school_id:schoolId})
    ]).then(([taskResult,alertResult,dashResult,lifeResult,handoverResult,schoolwideResult])=>{
      if(!active)return;
      const first=taskResult.error || alertResult.error || dashResult.error || lifeResult.error || handoverResult.error;
      if(first){setError(first.message);return;}

      setTasks(taskResult.data as TaskCenter);
      setAlerts(alertResult.data as AlertCenter);
      setDashboard(dashResult.data as ClassDashboard);
      const life=lifeResult.data as Lifecycle;
      setLifecycle(life);
      setHandover(handoverResult.data as HandoverWorkspace);

      const planned=life.academicYears.find((item)=>item.status==="PLANNED");
      setTargetYearId((current)=>life.academicYears.some((item)=>item.academicYearId===current && item.status==="PLANNED")?current:planned?.academicYearId || "");
      const hw=handoverResult.data as HandoverWorkspace;
      setHandoverTarget((current)=>hw.staff.some((item)=>item.personId===current)?current:hw.staff[0]?.personId || "");

      if(!schoolwideResult.error){
        const sw=schoolwideResult.data as Schoolwide;
        setSchoolwide(sw);
        setSchoolAssessment((current)=>sw.assessments.some((item)=>item.versionId===current)?current:sw.assessments[0]?.versionId || "");
        setSelectedClasses((current)=>current.filter((id)=>sw.classes.some((item)=>item.classGroupId===id)));
      }else{
        setSchoolwide(null);
      }
    });
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  const selectedTargetYear=useMemo(
    ()=>lifecycle?.academicYears.find((item)=>item.academicYearId===targetYearId) || null,
    [lifecycle,targetYearId]
  );
  const selectedStudentCount=useMemo(
    ()=>schoolwide?.classes.filter((item)=>selectedClasses.includes(item.classGroupId)).reduce((sum,item)=>sum+Number(item.studentCount || 0),0) || 0,
    [schoolwide,selectedClasses]
  );
  const visibleAlerts=useMemo(
    ()=>alerts?.items.filter((item)=>alertFilter==="ALL" || item.state==="OPEN") || [],
    [alerts,alertFilter]
  );

  async function run(key:string,fn:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>,success?:string){
    setBusy(key);setError("");setMessage("");
    try{
      const result=await fn();
      if(result.error)throw new Error(result.error.message);
      if(success)setMessage(success);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error ? err.message : "처리 중 오류가 발생했습니다.");
      return null;
    }finally{setBusy("");}
  }

  async function setAlertState(alertKey:string,action:"ACKNOWLEDGE"|"SNOOZE_1D"|"SNOOZE_7D"|"REOPEN"){
    await run("alert",()=>client.rpc("center_set_alert_state",{p_school_id:schoolId,p_alert_key:alertKey,p_action:action}));
  }

  async function assignSchoolAssessment(){
    if(!schoolAssessment || !selectedClasses.length)return;
    const result=await run("school-assessment",()=>client.rpc("center_schoolwide_assign_assessment",{
      p_school_id:schoolId,p_version_id:schoolAssessment,p_class_group_ids:selectedClasses
    }));
    if(result){
      const value=result as {requestedCount:number;succeededCount:number;skippedCount:number;failedCount:number};
      setMessage(`검사 일괄배정 완료 · 대상 ${value.requestedCount} / 배정 ${value.succeededCount} / 건너뜀 ${value.skippedCount} / 실패 ${value.failedCount}`);
    }
  }

  async function loadPreflight(){
    if(!targetYearId)return;
    setBusy("preflight");setError("");setMessage("");
    const {data,error}=await client.rpc("center_year_end_preflight",{p_school_id:schoolId,p_target_academic_year_id:targetYearId});
    setBusy("");
    if(error){setError(error.message);return;}
    setPreflight(data as Preflight);setCloseConfirm("");
  }

  async function finalizeYear(){
    if(!selectedTargetYear || closeConfirm.trim()!==selectedTargetYear.yearCode)return;
    const result=await run("close",()=>client.rpc("center_finalize_year_end",{
      p_school_id:schoolId,p_target_academic_year_id:targetYearId,p_note:closeNote.trim() || null
    }),"학년도 마감과 새 학년도 전환을 완료했습니다.");
    if(result){setPreflight(null);setCloseConfirm("");setCloseNote("");}
  }

  async function exportDataset(dataset:"STUDENTS"|"COUNSELING"|"BADGES"){
    setBusy("export-"+dataset);setError("");setMessage("");
    const {data,error}=await client.rpc("center_export_dataset",{p_school_id:schoolId,p_dataset:dataset});
    setBusy("");
    if(error){setError(error.message);return;}
    const rows=(data || []) as Array<Record<string,unknown>>;
    if(!rows.length){setMessage("내보낼 데이터가 없습니다.");return;}
    const dateKey=new Date().toISOString().slice(0,10);
    const label=dataset==="STUDENTS"?"학생현황":dataset==="COUNSELING"?"상담원장":"디지털배지원장";
    downloadCsv(rows,`RS_CAREER_${label}_${dateKey}.csv`);
    setMessage(`${label} ${rows.length}건을 엑셀용 CSV로 내보냈습니다.`);
  }

  async function createHandover(){
    if(!handoverTarget)return;
    const result=await run("handover",()=>client.rpc("center_create_staff_handover",{
      p_school_id:schoolId,p_target_person_id:handoverTarget,p_note:handoverNote.trim() || null
    }),"교사 인수인계 패키지를 전달했습니다.");
    if(result)setHandoverNote("");
  }

  async function acknowledge(packageId:string){
    await run("ack-"+packageId,()=>client.rpc("center_acknowledge_staff_handover",{p_school_id:schoolId,p_package_id:packageId}),"인수인계 확인을 기록했습니다.");
  }

  if(!tasks || !alerts || !dashboard || !lifecycle || !handover)return <p className="muted">업무함을 불러오는 중…</p>;

  return (
    <section className="operations-page">
      <header className="operations-head">
        <div><p className="eyebrow">Teacher Operations ERP</p><h2>업무함</h2><p>오늘 처리할 일, 운영 알림, 학교 전체 일괄업무와 학년도 마감까지 한곳에서 관리합니다.</p></div>
        <div className="operations-alert"><span>열린 알림</span><strong>{alerts.counts.open}</strong><small>우선 {alerts.counts.high}</small></div>
      </header>
      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="operations-metrics">
        <Metric label="미처리업무" value={tasks.counts.total}/>
        <Metric label="우선알림" value={alerts.counts.high}/>
        <Metric label="상담·후속" value={tasks.counts.overdueCounseling+tasks.counts.followUp}/>
        <Metric label="학적이동" value={tasks.counts.transfer}/>
        <Metric label="작업실패" value={tasks.counts.bulkFailure}/>
        <Metric label="배지업무" value={tasks.counts.badge}/>
      </div>

      <nav className="operations-tabs erp06-tabs">
        <button className={tab==="TASKS"?"active":""} onClick={()=>setTab("TASKS")}>통합 업무함</button>
        <button className={tab==="ALERTS"?"active":""} onClick={()=>setTab("ALERTS")}>알림센터</button>
        <button className={tab==="DASHBOARD"?"active":""} onClick={()=>setTab("DASHBOARD")}>학급 대시보드</button>
        {schoolwide && <button className={tab==="SCHOOLWIDE"?"active":""} onClick={()=>setTab("SCHOOLWIDE")}>학교 일괄업무</button>}
        <button className={tab==="EXPORT"?"active":""} onClick={()=>setTab("EXPORT")}>내보내기</button>
        <button className={tab==="YEAR_END"?"active":""} onClick={()=>setTab("YEAR_END")}>학년도 마감</button>
        <button className={tab==="HANDOVER"?"active":""} onClick={()=>setTab("HANDOVER")}>인수인계</button>
      </nav>

      {tab==="TASKS" && (
        <section className="operations-card">
          <div className="panel-title"><strong>오늘 처리할 일</strong><span>업무 발생상태를 그대로 보여줍니다.</span></div>
          <div className="operations-task-list">
            {tasks.items.map((item)=>(
              <button key={item.taskType+"-"+item.refId} className={item.priority==="HIGH"?"high":""} onClick={()=>navigate(item.actionPath)}>
                <span className="task-kind">{taskTypeLabel(item.taskType)}</span>
                <span className="task-copy"><strong>{item.displayName ? item.displayName+" · " : ""}{item.title}</strong><small>{item.subtitle || "확인이 필요합니다."}</small></span>
                <span className="task-due"><b>{priorityLabel(item.priority)}</b><small>{fmt(item.dueAt)}</small></span>
              </button>
            ))}
            {!tasks.items.length && <div className="teacher-clear-state"><strong>미처리 업무가 없습니다.</strong><span>현재 업무 큐가 비어 있습니다.</span></div>}
          </div>
        </section>
      )}

      {tab==="ALERTS" && (
        <section className="operations-card">
          <div className="panel-title">
            <div><strong>알림센터</strong><span>업무 알림과 학교 운영 이상징후를 개인별로 확인·숨김 처리합니다.</span></div>
            <div className="alert-filter"><button className={alertFilter==="OPEN"?"active":""} onClick={()=>setAlertFilter("OPEN")}>열린 알림 {alerts.counts.open}</button><button className={alertFilter==="ALL"?"active":""} onClick={()=>setAlertFilter("ALL")}>전체</button></div>
          </div>
          <div className="alert-center-list">
            {visibleAlerts.map((item)=>(
              <article key={item.alertKey} className={item.severity==="HIGH"?"high":item.state.toLowerCase()}>
                <div className="alert-copy">
                  <span>{alertCategoryLabel(item.category)} · {item.severity==="HIGH"?"우선":"일반"}</span>
                  <strong>{item.title}</strong>
                  <p>{item.message}</p>
                  {item.dueAt && <small>{fmt(item.dueAt)}</small>}
                  {item.state==="SNOOZED" && <small>숨김 해제 {fmt(item.snoozedUntil)}</small>}
                </div>
                <div className="alert-actions">
                  <button onClick={()=>navigate(item.actionPath)}>업무 열기</button>
                  {item.state==="OPEN" ? (
                    <>
                      <button onClick={()=>setAlertState(item.alertKey,"SNOOZE_1D")}>1일 숨김</button>
                      <button className="primary" onClick={()=>setAlertState(item.alertKey,"ACKNOWLEDGE")}>확인</button>
                    </>
                  ) : (
                    <button onClick={()=>setAlertState(item.alertKey,"REOPEN")}>다시 열기</button>
                  )}
                </div>
              </article>
            ))}
            {!visibleAlerts.length && <p className="case-empty">표시할 알림이 없습니다.</p>}
          </div>
        </section>
      )}

      {tab==="DASHBOARD" && (
        <section className="operations-card">
          <div className="panel-title"><strong>학급별 운영현황</strong><span>검사·활동·상담·배지를 한 행에서 비교합니다.</span></div>
          <div className="class-dashboard-table">
            <div className="class-dashboard-row head"><span>학급</span><span>학생</span><span>진로검사</span><span>활동완료</span><span>검증결과물</span><span>상담</span><span>디지털배지</span><span>수령문제</span></div>
            {dashboard.classes.map((item)=>(
              <button className="class-dashboard-row" key={item.classGroupId} onClick={()=>navigate("/classes")}>
                <strong>{item.name}</strong>
                <span>{item.studentCount}</span>
                <span>{item.assessmentCompleted}/{item.assessmentAssigned}</span>
                <span>{item.participationCompleted}/{item.participationCount}</span>
                <span>{item.verifiedEvidence}</span>
                <span className={item.overdueCounseling>0?"warn":""}>{item.openCounseling} Case · 예정 {item.scheduledCounseling}{item.overdueCounseling? ` · 지연 ${item.overdueCounseling}`:""}</span>
                <span>{item.activeBadges}</span>
                <span className={item.badgeDeliveryWaiting>0?"warn":""}>{item.badgeDeliveryWaiting}</span>
              </button>
            ))}
            {!dashboard.classes.length && <p className="case-empty">표시할 학급이 없습니다.</p>}
          </div>
        </section>
      )}

      {tab==="SCHOOLWIDE" && schoolwide && (
        <div className="schoolwide-layout">
          <section className="operations-card">
            <div className="panel-title"><div><strong>학교 단위 진로검사 일괄배정</strong><span>반을 명시적으로 선택한 뒤 한 번의 서버 Job으로 배정합니다.</span></div></div>
            <label className="schoolwide-assessment-select">검사
              <select value={schoolAssessment} onChange={(e)=>setSchoolAssessment(e.target.value)}>
                {schoolwide.assessments.map((item)=><option key={item.versionId} value={item.versionId}>{item.instrumentName} · {item.title}</option>)}
              </select>
            </label>
            <div className="schoolwide-classes">
              {schoolwide.classes.map((item)=>(
                <label key={item.classGroupId} className={selectedClasses.includes(item.classGroupId)?"selected":""}>
                  <input type="checkbox" checked={selectedClasses.includes(item.classGroupId)} onChange={()=>setSelectedClasses((current)=>current.includes(item.classGroupId)?current.filter((id)=>id!==item.classGroupId):[...current,item.classGroupId])}/>
                  <span><strong>{item.name}</strong><small>{item.studentCount}명</small></span>
                </label>
              ))}
            </div>
            <div className="schoolwide-actions">
              <button className="secondary" onClick={()=>setSelectedClasses(schoolwide.classes.map((item)=>item.classGroupId))}>전체 학급 선택</button>
              <button className="secondary" onClick={()=>setSelectedClasses([])}>선택 해제</button>
              <button className="primary" disabled={busy!=="" || !schoolAssessment || !selectedClasses.length} onClick={assignSchoolAssessment}>선택 {selectedClasses.length}개 학급 · {selectedStudentCount}명 검사 배정</button>
            </div>
          </section>

          <section className="operations-card">
            <div className="panel-title"><strong>최근 학교 일괄작업</strong><span>검사 배정 Job</span></div>
            <div className="schoolwide-job-list">
              {schoolwide.recentJobs.map((job)=>(
                <article key={job.jobId}>
                  <span><strong>{job.status}</strong><small>{fmt(job.createdAt)} · 재시도 {job.retryNumber}</small></span>
                  <dl><div><dt>요청</dt><dd>{job.requestedCount}</dd></div><div><dt>성공</dt><dd>{job.succeededCount}</dd></div><div><dt>건너뜀</dt><dd>{job.skippedCount}</dd></div><div><dt>실패</dt><dd>{job.failedCount}</dd></div></dl>
                  {["PARTIAL","FAILED"].includes(job.status) && <button onClick={()=>navigate("/classes?tab=jobs")}>실패 상세</button>}
                </article>
              ))}
              {!schoolwide.recentJobs.length && <p className="case-empty">검사 일괄배정 이력이 없습니다.</p>}
            </div>
          </section>
        </div>
      )}

      {tab==="EXPORT" && (
        <div className="export-grid">
          <ExportCard title="학생 현황" description="학년·반, 활동, 검사, 상담, 유효 배지를 학생별로 내보냅니다." button="학생현황 CSV" busy={busy!==""} onClick={()=>exportDataset("STUDENTS")}/>
          <ExportCard title="상담 원장" description="상담일시, 유형, 중요도, 주제, 다음 조치와 후속상담일을 내보냅니다." button="상담원장 CSV" busy={busy!==""} onClick={()=>exportDataset("COUNSELING")}/>
          <ExportCard title="디지털배지 원장" description="발급학교·학년도·학년·반, 상태, 검증주소와 수령상태를 내보냅니다." button="배지원장 CSV" busy={busy!==""} onClick={()=>exportDataset("BADGES")}/>
        </div>
      )}

      {tab==="YEAR_END" && (
        <section className="operations-card year-end-card">
          <div className="panel-title"><div><strong>학년도 마감·전환</strong><span>학적이동 준비가 끝난 뒤 실행합니다. 미처리 상담·배지업무는 이월 기록으로 보존합니다.</span></div></div>
          {!lifecycle.canManage ? <p className="case-empty">학교 관리자만 학년도 마감을 실행할 수 있습니다.</p> : (
            <>
              <div className="year-end-picker">
                <select value={targetYearId} onChange={(e)=>{setTargetYearId(e.target.value);setPreflight(null);setCloseConfirm("");}}>
                  <option value="">다음 학년도 선택</option>
                  {lifecycle.academicYears.filter((item)=>item.status==="PLANNED").map((year)=><option key={year.academicYearId} value={year.academicYearId}>{year.yearCode} · {year.startsOn}</option>)}
                </select>
                <button className="secondary" disabled={busy!=="" || !targetYearId} onClick={loadPreflight}>마감 사전점검</button>
              </div>
              {preflight && (
                <div className="year-end-preflight">
                  <div className="preflight-status"><strong>{preflight.sourceYearCode} → {preflight.targetYearCode}</strong><b className={preflight.canClose?"pass":"blocked"}>{preflight.canClose?"마감 가능":"조치 필요"}</b></div>
                  <div className="preflight-grid">
                    <Check label="미완료 전입·전출" value={preflight.blockers.openTransfers} blocker/>
                    <Check label="학년이동 미준비 학생" value={preflight.blockers.unpreparedStudents} blocker/>
                    <Check label="준비된 전환 Batch" value={preflight.blockers.preparedTransitionBatches}/>
                    <Check label="열린 상담 Case 이월" value={preflight.carryover.openCounselingCases}/>
                    <Check label="예약 상담 이월" value={preflight.carryover.scheduledCounseling}/>
                    <Check label="실패 작업 이월" value={preflight.carryover.failedBulkJobs}/>
                    <Check label="배지 승인업무 이월" value={preflight.carryover.pendingBadgeManagement}/>
                  </div>
                  {preflight.blockers.targetNotStarted && <p className="year-end-warning">다음 학년도 시작일 {preflight.targetStartsOn} 전에는 마감 적용할 수 없습니다.</p>}
                  {preflight.canClose && selectedTargetYear && (
                    <div className="year-end-confirm">
                      <label>마감 메모<textarea rows={3} value={closeNote} onChange={(e)=>setCloseNote(e.target.value)} placeholder="학년도 마감 메모(선택)" /></label>
                      <label>실행 확인<input value={closeConfirm} onChange={(e)=>setCloseConfirm(e.target.value)} placeholder={selectedTargetYear.yearCode+" 입력"} /><small>실행하려면 다음 학년도 코드 <b>{selectedTargetYear.yearCode}</b>를 입력하세요.</small></label>
                      <button className="primary danger-action" disabled={busy!=="" || closeConfirm.trim()!==selectedTargetYear.yearCode} onClick={finalizeYear}>학년도 마감 + {selectedTargetYear.yearCode} 시작</button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {tab==="HANDOVER" && (
        <div className="handover-layout">
          <section className="operations-card">
            <div className="panel-title"><strong>인수인계 보내기</strong><span>담당학생·열린 상담 Case·예약 상담을 패키지로 전달합니다.</span></div>
            {handover.staff.length ? (
              <div className="handover-form">
                <select value={handoverTarget} onChange={(e)=>setHandoverTarget(e.target.value)}>{handover.staff.map((staff)=><option key={staff.personId} value={staff.personId}>{staff.displayName}</option>)}</select>
                <textarea rows={4} value={handoverNote} onChange={(e)=>setHandoverNote(e.target.value)} placeholder="인수인계 메모" />
                <button className="primary" disabled={busy!=="" || !handoverTarget} onClick={createHandover}>인수인계 패키지 전달</button>
                <p className="handover-note">인수인계는 업무정보 전달 기록입니다. 학생 접근권한은 학급·담당교사 배정에서 별도로 변경합니다.</p>
              </div>
            ) : <p className="case-empty">인수인계할 다른 교직원 계정이 없습니다.</p>}
          </section>
          <section className="operations-card">
            <div className="panel-title"><strong>인수인계 이력</strong><span>{handover.packages.length}건</span></div>
            <div className="handover-list">
              {handover.packages.map((item)=>(
                <article key={item.packageId} className={item.incoming && item.status==="SENT"?"incoming":""}>
                  <header><span><strong>{item.sourceName} → {item.targetName}</strong><small>{fmt(item.createdAt)} · {item.itemCount}개 항목</small></span><b>{item.status==="SENT"?"확인 대기":item.status==="ACKNOWLEDGED"?"확인 완료":item.status}</b></header>
                  {item.note && <p>{item.note}</p>}
                  <div className="handover-items">{item.items.slice(0,8).map((sub)=><span key={sub.itemId}><b>{sub.itemType==="LEARNER"?"학생":sub.itemType==="COUNSELING_CASE"?"상담 Case":"예약상담"}</b>{sub.title}</span>)}</div>
                  {item.incoming && item.status==="SENT" && <button className="primary" disabled={busy!==""} onClick={()=>acknowledge(item.packageId)}>인수인계 확인</button>}
                </article>
              ))}
              {!handover.packages.length && <p className="case-empty">인수인계 이력이 없습니다.</p>}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

function Metric({label,value}:{label:string;value:number}){return <div className="ops-metric"><span>{label}</span><strong>{value}</strong></div>}
function ExportCard({title,description,button,busy,onClick}:{title:string;description:string;button:string;busy:boolean;onClick():void}){return <section className="export-card"><span className="export-icon">⇩</span><h3>{title}</h3><p>{description}</p><button className="primary" disabled={busy} onClick={onClick}>{button}</button></section>}
function Check({label,value,blocker=false}:{label:string;value:number;blocker?:boolean}){return <div className={blocker&&value>0?"preflight-check blocker":"preflight-check"}><span>{label}</span><strong>{value}</strong></div>}
